import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import { getAgencyConfig } from '../services/agencyConfig';

export interface OasisSigningRequirements {
  /** Clinicians must clear OASIS CMS fatals before signing. */
  clinician: boolean;
  /** QA must clear OASIS CMS fatals before signing or completing. */
  qa: boolean;
}

const NOT_REQUIRED: OasisSigningRequirements = { clinician: false, qa: false };

/**
 * Whether this agency makes clinicians and/or QA clear the All CMS scrubber
 * before signing an OASIS chart.
 *
 * Reports "not required" until the config resolves, and on failure. That only
 * decides whether the visit page stops a signature before the password prompt;
 * the backend enforces the rule either way, and its refusal opens the same
 * explanation.
 */
export function useOasisSigningRequirements(): OasisSigningRequirements {
  const agencyId = useStore(useShallow(s => s.user?.agency?.id));
  const [requirements, setRequirements] =
    useState<OasisSigningRequirements>(NOT_REQUIRED);

  useEffect(() => {
    if (!agencyId) {
      setRequirements(NOT_REQUIRED);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const cfg = await getAgencyConfig();
        if (!cancelled) {
          setRequirements({
            clinician: cfg.require_clean_oasis_for_clinician_signature ?? false,
            qa: cfg.require_clean_oasis_for_qa_signature ?? true,
          });
        }
      } catch {
        if (!cancelled) setRequirements(NOT_REQUIRED);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  return requirements;
}
